/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { IdentityLogEvent } from '@localstack/types';
import type { IdentityProvider } from '@localstack/types';
import type { LicenseAssignment } from '@localstack/types';
import type { LicenseAssignmentInfo } from '@localstack/types';
import type { SelfAssignLicenseRequest } from '@localstack/types';
import type { SelfAssignmentPreflight } from '@localstack/types';
import type { SetLicenseAssignmentsRequest } from '@localstack/types';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class OrganizationsService {
  /**
   * @param orgId
   * @returns LicenseAssignment
   * @throws ApiError
   */
  public static listLicenseAssignments(orgId: any): CancelablePromise<Array<LicenseAssignment>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/organizations/{org_id}/license-assignments',
      path: {
        org_id: orgId,
      },
    });
  }

  /**
   * @param orgId
   * @returns LicenseAssignmentInfo
   * @throws ApiError
   */
  public static listLicenseAssignmentsForUser(orgId: any): CancelablePromise<Array<LicenseAssignmentInfo>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/organizations/{org_id}/license-assignments/me',
      path: {
        org_id: orgId,
      },
    });
  }

  /**
   * @param orgId
   * @param userId
   * @param requestBody
   * @returns LicenseAssignment
   * @throws ApiError
   */
  public static setLicenseAssignmentsForUser(
    orgId: any,
    userId: any,
    requestBody?: SetLicenseAssignmentsRequest,
  ): CancelablePromise<Array<LicenseAssignment>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/organizations/{org_id}/license-assignments/{user_id}',
      path: {
        org_id: orgId,
        user_id: userId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @param requestBody
   * @returns LicenseAssignment
   * @throws ApiError
   */
  public static selfAssignLicense(
    orgId: any,
    requestBody?: SelfAssignLicenseRequest,
  ): CancelablePromise<LicenseAssignment> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/organizations/{org_id}/my-license-assignment',
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @returns SelfAssignmentPreflight
   * @throws ApiError
   */
  public static preflightSelfAssignLicense(orgId: any): CancelablePromise<SelfAssignmentPreflight> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/organizations/{org_id}/my-license-assignment/preflight',
      path: {
        org_id: orgId,
      },
    });
  }

  /**
   * @param orgId
   * @param assignmentId
   * @returns LicenseAssignment
   * @throws ApiError
   */
  public static unassignLicense(orgId: any, assignmentId: any): CancelablePromise<LicenseAssignment> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/organizations/{org_id}/license-assignments/{assignment_id}',
      path: {
        org_id: orgId,
        assignment_id: assignmentId,
      },
    });
  }

  /**
   * @param orgId
   * @param cursor Opaque pagination cursor returned by a previous response.
   * @returns any
   * @throws ApiError
   */
  public static listIdentityEvents(
    orgId: any,
    cursor?: string,
  ): CancelablePromise<{
    items?: Array<IdentityLogEvent>;
    next_cursor?: string | null;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/organizations/{org_id}/events',
      path: {
        org_id: orgId,
      },
      query: {
        cursor: cursor,
      },
    });
  }

  /**
   * @param email
   * @returns IdentityProvider
   * @throws ApiError
   */
  public static listIdentityProvidersForEmail(email: any): CancelablePromise<Array<IdentityProvider>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/organizations/idps/{email}',
      path: {
        email: email,
      },
    });
  }

  /**
   * @param orgId
   * @returns IdentityProvider
   * @throws ApiError
   */
  public static listIdentityProviders(orgId: any): CancelablePromise<Array<IdentityProvider>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/organizations/{org_id}/idps',
      path: {
        org_id: orgId,
      },
    });
  }

  /**
   * @param orgId
   * @param idpNameOrId
   * @returns IdentityProvider
   * @throws ApiError
   */
  public static getIdentityProvider(orgId: any, idpNameOrId: any): CancelablePromise<IdentityProvider> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/organizations/{org_id}/idps/{idp_name_or_id}',
      path: {
        org_id: orgId,
        idp_name_or_id: idpNameOrId,
      },
    });
  }

  /**
   * @param orgId
   * @param idpName
   * @param requestBody
   * @returns IdentityProvider
   * @throws ApiError
   */
  public static storeIdentityProvider(
    orgId: any,
    idpName: any,
    requestBody?: IdentityProvider,
  ): CancelablePromise<IdentityProvider> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/v1/organizations/{org_id}/idps/{idp_name}',
      path: {
        org_id: orgId,
        idp_name: idpName,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @param idpName
   * @returns IdentityProvider
   * @throws ApiError
   */
  public static deleteIdentityProvider(orgId: any, idpName: any): CancelablePromise<IdentityProvider> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/organizations/{org_id}/idps/{idp_name}',
      path: {
        org_id: orgId,
        idp_name: idpName,
      },
    });
  }

  /**
   * @returns any
   * @throws ApiError
   */
  public static listOrganizationRestrictions(): CancelablePromise<
    Array<{
      id?: string;
      org_id?: string;
      reason?: string;
      created?: number;
    }>
  > {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/organizations/restrictions',
    });
  }
}
